import { Combobox, Input, InputBase, useCombobox } from "@mantine/core";
import { UserActivationStatus } from "@src/gql/graphql";
import clsx from "clsx";
import type { Role } from "./whiteboard-invite-schema";

interface ShareWhiteboardComboboxProps {
	value: Role;
	onChange: (value: Role) => void;
	disableEditor?: boolean;
	availableOptions: Role[];
}

export function ShareWhiteboardCombobox(props: ShareWhiteboardComboboxProps) {
	const { value, onChange, availableOptions, disableEditor } = props;

	const combobox = useCombobox({
		onDropdownClose: () => combobox.resetSelectedOption(),
	});

	const options = availableOptions.map((item) => {
		return (
			<Combobox.Option
				key={item}
				value={item}
				className={item === "Remove" ? "text-danger" : undefined}
				disabled={item === "Waiting" || (disableEditor && item === "Editor")}
			>
				{item}
			</Combobox.Option>
		);
	});

	return (
		<Combobox
			zIndex={2500}
			store={combobox}
			onOptionSubmit={(val) => {
				const role = val as Role;
				combobox.closeDropdown();
				onChange(role);
			}}
		>
			<Combobox.Target>
				<InputBase
					value={value}
					component="button"
					type="button"
					pointer
					rightSection={<Combobox.Chevron />}
					rightSectionPointerEvents="none"
					onClick={() => combobox.toggleDropdown()}
					classNames={{ input: "focus:border-brand-primary" }}
					className={clsx(
						value === "Waiting" && "[&_button]:text-text-primary/30",
						value === "Remove" && "[&_button]:text-danger",
					)}
				>
					{value ?? <Input.Placeholder>Pick value</Input.Placeholder>}
				</InputBase>
			</Combobox.Target>

			<Combobox.Dropdown>
				<Combobox.Options>{options}</Combobox.Options>
			</Combobox.Dropdown>
		</Combobox>
	);
}

export function ShareWhiteboardUserCombobox(props: {
	activationStatus: UserActivationStatus;
	value: Role;
	onChange: (value: Role) => void;
	disableEditor?: boolean;
}) {
	const { activationStatus, value, onChange, disableEditor } = props;

	const isRegistered = activationStatus === UserActivationStatus.Registered;

	const registeredComboboxOptions: Role[] = [
		"Viewer",
		"Editor",
		"Owner",
		"Remove",
	];

	const invitedComboboxOptions: Role[] = ["Waiting", "Remove"];

	const comboboxOptions = isRegistered
		? registeredComboboxOptions
		: invitedComboboxOptions;

	const initalItem: Role = isRegistered ? value : "Waiting";

	return (
		<ShareWhiteboardCombobox
			value={initalItem}
			onChange={onChange}
			availableOptions={comboboxOptions}
			disableEditor={disableEditor}
		/>
	);
}
